import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { MailPlus } from 'lucide-react';
import { useAuthStore } from '../auth/AuthBoundary';
import { invitationsRowLabel } from '../screens/invitations/invitation-words';

/**
 * Under the person's companies, while an invitation waits for them: the way back to answer it
 * (nobody joins a company without saying so, so a company that invited them is not in the list
 * until they accept). Asked each time the menu opens; nothing is shown when nothing waits or the
 * read fails.
 */
export function InvitationsRow({
  index,
  onClose,
}: {
  index: number;
  onClose: () => void;
}): ReactNode {
  const count = useInvitationCount();
  const navigate = useNavigate();
  if (count === 0) return null;
  const open = () => {
    onClose();
    void navigate({ to: '/invitations', search: {} });
  };
  return (
    <button
      type="button"
      role="menuitem"
      className="profile__out"
      style={{ '--i': index } as CSSProperties}
      onClick={open}
    >
      <span className="profile__icon" aria-hidden="true">
        <MailPlus size={15} />
      </span>
      <span>{invitationsRowLabel(count)}</span>
    </button>
  );
}

/** How many invitations wait for the person signed in; 0 until the answer comes, and when it does not. */
function useInvitationCount(): number {
  const store = useAuthStore();
  const [count, setCount] = useState(0);
  useEffect(() => {
    let live = true;
    store.invitations().then(
      (list) => live && setCount(list.length),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [store]);
  return count;
}
